import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import Table from "../../../components/Table/Table";
import { EditActions, editActions } from "../../../actions/editActions";
import { isEditActiveSelector } from "../../../reducers/editReducer";
import { Dimmer } from "semantic-ui-react";
import { mainActions, MainActions } from "../../../actions/mainActions";
import EditEnumeration from "../../Editing/EditEnumeration";
import EditEnumerationItem from "../../Editing/EditEnumerationItem";
import styles from "./CommonEnumerations.module.scss";
import AddButton from "../../../components/AddButton/AddButton";
import { createEmptyCommonEnumerationItem } from "../../../utils/pdApiUtils";
import { getNotificationObject, hasWriteAccess } from "../../../utils/utils";
import { error } from "react-notification-system-redux";

type StateProps = {
  pdApi: State.PdApi | undefined;
  columns: State.ColumnMap;
  mainState: State.Main;
  editedObjectId: string;
  parentObjectId: string;
  editedObjectType: string;
  isEditActive: boolean;
  mainActions: typeof mainActions;
  activeUser: Api.User | undefined;
};

type DispatchProps = {
  editActions: typeof editActions;
  showError: typeof error;
};

type OwnProps = {
  subMenuRoute: string;
  groupName?: Api.GroupName;
  toggleModal: () => void;
};

// Props of the component
export type CommonEnumerationsProps = StateProps & DispatchProps & OwnProps;

type CommonEnumerationsState = {
  isModalOpen: boolean;
};

export class CommonEnumerations extends React.Component<
  CommonEnumerationsProps,
  CommonEnumerationsState
> {
  state = {
    isModalOpen: false
  };

  componentWillUnmount(): void {
    this.props.mainActions.setLastEditedObjectId(undefined, undefined);
  }

  onEditClick = (
    e: { stopPropagation: () => void },
    row: { Name: string; Edit: Api.CommonEnumeration }
  ): void => {
    this.props.editActions.setEditedType("enumeration");
    this.props.editActions.startEditing(row.Edit as Api.CommonEnumeration);
    e.stopPropagation();
  };

  SetEditButtonHandler = (arg0: State.Column[]): State.Column[] => {
    const editColumn = arg0.find(col => col.id === "Edit");
    if (editColumn) {
      // eslint-disable-next-line react/display-name, react/prop-types
      editColumn.Cell = ({ row }) => (
        <div className={styles.basic}>
          <button onClick={e => this.onEditClick(e, row)}>Edit</button>
        </div>
      );
    }
    return arg0;
  };

  selectEditor(toggleModal: () => void): JSX.Element {
    const { editedObjectType } = this.props;
    switch (editedObjectType) {
      case "enumerator":
        return (
          <EditEnumerationItem
            columns={this.props.columns}
            toggleModal={toggleModal}
            parentObjectId={this.props.parentObjectId}
          />
        );
      case "enumeration":
        return <EditEnumeration toggleModal={toggleModal} />;
      default:
        return <span>Error in editor selection</span>;
    }
  }

  isEnumerationSelected = (commonEnumeration: Api.CommonEnumeration): boolean => {
    const { parentObjectId } = this.props;
    if (
      parentObjectId === commonEnumeration.id ??
      this.props.mainState.lastSelectedParentObjectId
    ) {
      return true;
    } else {
      return false;
    }
  };

  isEnumerationItemSelected = (enumerationItem: Api.CommonEnumerationItem): boolean => {
    const { editedObjectId } = this.props;
    if (editedObjectId === enumerationItem.id ?? this.props.mainState.lastEditedObjectId) {
      return true;
    } else {
      return false;
    }
  };

  handleParentCommonEnumerationSelection = (
    selectedCommonEnumeration: Api.CommonEnumeration
  ): void => {
    this.props.editActions.selectParent(selectedCommonEnumeration);
  };

  handleCommonEnumerationItemSelection = (selectedItem: Api.CommonEnumerationItem): void => {
    this.props.editActions.setEditedType("enumerator");
    this.props.editActions.select(selectedItem);
  };

  onAddNewEnumeratorClick(): void {
    const { pdApi } = this.props;
    this.props.editActions.setEditedType("enumerator");
    if (!pdApi) return;
    try {
      const emptyCommonEnumerationItem = createEmptyCommonEnumerationItem(
        this.props.parentObjectId,
        pdApi
      );
      this.props.editActions.startEditing(emptyCommonEnumerationItem, true);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e) {
      this.props.showError(getNotificationObject("error", e instanceof Error ? e.message : String(e)));
    }
  }

  render(): JSX.Element {
    const { columns, pdApi, mainState, isEditActive, toggleModal } = this.props;
    const { enumerations } = pdApi || { enumerations: [] };
    const { editSize } = mainState;
    const rows: Api.CommonEnumeration[] = [...enumerations].sort(function(
      a: Api.CommonEnumeration,
      b: Api.CommonEnumeration
    ) {
      const targetComparison = a.target.localeCompare(b.target);
      const nameComparison = a.name.localeCompare(b.name);
      return !targetComparison ? nameComparison : targetComparison;
    });
    const selectedColumns: State.Column[] = this.SetEditButtonHandler(
      columns["Common Enumerations"]
    );

    // Split parameters into parent and sub cageories, display parents left
    const parentRows = rows;
    let subRows = null;
    if (this.props.parentObjectId && this.props.pdApi) {
      subRows = this.props.pdApi.enumerationItems
        .filter(x => x.enumerationId === this.props.parentObjectId)
        .sort((a, b) => (a.enumValue < b.enumValue ? -1 : a.enumValue > b.enumValue ? 1 : 0));
    }
    return (
      <div className="flex-1">
        <div className={`flex-1 ${subRows ?  "parameterTable" : ""}`}>
          <div className={styles.container}>
            <Table
              rows={parentRows}
              columns={selectedColumns}
              isHidingTextActive={true}
              handleRowSelection={
                isEditActive
                  ? () => {
                      /*  */
                    }
                  : this.handleParentCommonEnumerationSelection
              }
              isRowSelected={this.isEnumerationSelected}
              pageSize={mainState.pageSize}
              setHeight="fit-content"
            />
          </div>
          {subRows && (
            <div className={styles.container}>
              <Table
                rows={subRows}
                columns={columns["Common Enumeration Items"]}
                isHidingTextActive={true}
                handleRowSelection={
                  isEditActive
                    ? () => {
                        /*  */
                      }
                    : this.handleCommonEnumerationItemSelection
                }
                isRowSelected={this.isEnumerationItemSelected}
                pageSize={10000}
                disablePagination={true}
                setHeight="fit-content"
              />

              {hasWriteAccess(this.props.activeUser) && (
                <div className={styles.addButtonContainer}>
                  <AddButton
                    title="Add enumerator"
                    onClick={() => this.onAddNewEnumeratorClick()}
                    id="addEnumeratorButton"
                  />
                </div>
              )}
            </div>
          )}
        </div>
        {isEditActive && (
          <Dimmer active={true} page={true}>
            <div className="edit-view" style={{ flex: editSize , width : editSize == 0.3 ? "500px" : "1000px" }}>
              {this.selectEditor(toggleModal)}
            </div>
          </Dimmer>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state: State.Root) => ({
  columns: state.columns,
  mainState: state.main,
  isEditActive: isEditActiveSelector(state.editView),
  editedObjectType: state.editView.editedObjectType,
  activeUser: state.main.activeUser
});

const mapDispatchToProps = (dispatch: Dispatch<EditActions | MainActions>) => ({
  editActions: bindActionCreators({ ...editActions }, dispatch),
  mainActions: bindActionCreators({ ...mainActions }, dispatch),
  showError: bindActionCreators(error, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(CommonEnumerations);

